<template>
  <div class="view" v-if="canView" v-cloak>
    <GridPanelHeader
      :title="$t('apia.phytosanitary_register.title')"
      :grid-ref="$refs[gridRefKey]"
      :show-add="false"
      :show-filter="false"
      :show-columns="false"
    />
    <div class="row dash_filter pt-1 pb-2" style="display: flex; align-items: center">
      <div class="form-group text-left season_year_select col-3" style="margin-right: 20px">
        <DxSelectBox
          v-model="local_season_uuid"
          :data-source="seasons"
          display-expr="name"
          value-expr="uuid"
          :on-value-changed="onSeasonChanged"
          placeholder=""
        />
      </div>
      <div class="form-group text-right department_select col-6">
        <DxTagBox
          v-model="selected_departments"
          :data-source="departments"
          display-expr="name"
          value-expr="uuid"
          :show-clear-button="true"
          :search-enabled="true"
          :placeholder="$t('common.select_department')"
          @value-changed="onDepartmentsChanged"
        />
      </div>
    </div>

    <DxDataGrid
      :data-source="report_dataset"
      :remote-operations="gridRemoteOperations"
      :showBorders="true"
      :show-column-lines="true"
      :show-row-lines="false"
      :word-wrap-enabled="true"
      :row-alternation-enabled="true"
      :ref="gridRefKey"
      :element-attr="gridAttributes"
      @exporting="onExporting"
      key-expr="UUID"
    >
      <DxColumn
        dataField="date_of_treatment"
        caption="Data efectuarii tratamentului (ziua, luna, anul)"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      >
      </DxColumn>
      <DxColumn
        dataField="culture_and_field"
        caption="Cultura si locul unde este situat terenul"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      >
      </DxColumn>
      <DxColumn
        dataField="application_time"
        caption="Timpul aplicarii"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      >
      </DxColumn>

      <DxColumn caption="Tratamentul efectuat" alignment="center">
        <DxColumn
          data-field="damage_name"
          caption="Agentul de daunare: boala/daunatorul/buruieni"
          css-class="responsive-column"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="ppp_name"
          caption="Denumire ppp folosit"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="dose"
          caption="Doza omologata/doza folosita"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="area_ha"
          caption="Suprafata (ha)"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
        <DxColumn
          data-field="quantity"
          caption="Cantitati utilizate (kg, l)"
          :allowHeaderFiltering="false"
          :filter-operations="['contains']"
          :allowSorting="false"
          alignment="center"
        />
      </DxColumn>

      <DxColumn
        dataField="responsible_person"
        caption="Numele, prenumele persoanei responsabile de efectuarea tratamentului, semnatura"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      ></DxColumn>
      <DxColumn
        dataField="harvest_start_date"
        caption="Data începerii recoltarii produsului agricol"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      >
      </DxColumn>
      <DxColumn
        dataField="document_number_and_date"
        caption="Nr. si data documentului prin care s-a dat în consum populatiei"
        :allowHeaderFiltering="false"
        :filter-operations="['contains']"
        :allowSorting="false"
        alignment="center"
      >
      </DxColumn>

      <DxExport :enabled="false" :allow-export-selected-data="false" :fileName="'RegFit'" />
      <DxPaging :total="3500" :page-size="15" />
      <DxPager
        :visible="true"
        :allowed-page-sizes="pageSizes"
        display-mode="compact"
        :info-text="$t('common.page_info')"
        :show-page-size-selector="true"
        :show-info="true"
        :show-navigation-buttons="true"
      />
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="true" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
      <DxGroupPanel :visible="false" :empty-panel-text="$t('common.group_panel_placeholder')" />
      <DxGrouping :auto-expand-all="true" />
      <DxScrolling column-rendering-mode="virtual" />
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import DxDataGrid, {
  DxColumn,
  DxPaging,
  DxPager,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxButton,
  DxPopup,
  DxToolbar,
  DxItem,
  DxExport,
  DxGroupPanel,
  DxGrouping,
  DxScrolling,
} from "devextreme-vue/data-grid";
import { canView, canDelete, canEdit, permissions, permissionTypes } from "~/helpers/Permissions";
import Forbidden from "~/pages/errors/Forbidden.vue";
import { format } from "date-fns";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver-es";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxTagBox } from "devextreme-vue/tag-box";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "PhytosanitaryRegister",
  components: {
    DxDataGrid,
    DxColumn,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxButton,
    DxPaging,
    DxPager,
    DxPopup,
    DxToolbar,
    DxItem,
    Forbidden,
    DxExport,
    DxGroupPanel,
    DxGrouping,
    DxScrolling,
    DxSelectBox,
    DxTagBox,
    GridPanelHeader,
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid  phytosanitary-grid",
      },
      gridRefKey: "phytosanitaryGrid",
      report_dataset: [],
      gridRemoteOperations: {
        paging: false,
        filtering: false,
        sorting: false,
      },
      pageSizes: [15, 25, 100],
      local_season_uuid: null,
      selected_departments: [],
    };
  },
  watch: {
    seasons: {
      handler: function (newVal) {
        if (newVal.length > 0) {
          this.local_season_uuid = newVal[0].uuid;
          this.load();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      seasons: "seasons/seasons",
      departments: "company/departments",
    }),
    config() {
      return {
        permission: permissions.APIA,
        permission_type: permissionTypes.access,
      };
    },
    canView() {
      return canView(this);
    },
    canEdit() {
      return canEdit(this);
    },
    canDelete() {
      return canDelete(this);
    },
  },
  methods: {
    ...mapActions({
      getReportsData: "reporting/getReportsData",
      getSeasons: "seasons/getSeasons",
      getDepartments: "company/getDepartments",
    }),
    async load() {
      if (!this.local_season_uuid) {
        this.report_dataset = [];
        return;
      }

      let payload = {
        limit: 999999,
        name: "Phytosanitary_Register",
        args: {
          season_uuid: this.local_season_uuid,
          department_uuids: this.selected_departments.join(","),
        },
      };

      try {
        this.getReportsData(payload).then((response) => {
          this.report_dataset = response;
        });
      } catch (error) {
        console.error("Error fetching data:", error);
        throw error;
      }
    },
    onExporting(e) {
      e.fileName = "RegFit_" + format(new Date(), "yyyy_MM_dd_hh_mm_ss");

      if (e.format === "xlsx") {
        //const fields = ["deleted_at", "finished_weighing", "has_stock_movement"];
        const workbook = new Workbook();
        const worksheet = workbook.addWorksheet("RegFit");

        // Visible column count (so we can merge header cells across all columns)
        const visibleCols = e.component.getVisibleColumns().filter((c) => c.type !== "group" && c.dataField !== undefined);
        const colCount = visibleCols.length;

        // Helper to merge a full-width row and set value
        const put = (rowIndex, text, opts = {}) => {
          const startCell = worksheet.getCell(rowIndex, 1);
          const endCell = worksheet.getCell(rowIndex, colCount);
          worksheet.mergeCells(rowIndex, 1, rowIndex, colCount);
          startCell.value = text;
          startCell.alignment = { wrapText: true, horizontal: opts.center ? "center" : "left", vertical: "top" };
          if (opts.bold) startCell.font = { bold: true };
          if (opts.italic) startCell.font = { italic: true };
          if (opts.size) startCell.font = { ...(startCell.font || {}), size: opts.size };
        };

        const putRange = (rowIndex, fromCol, toCol, text, opts = {}) => {
          worksheet.mergeCells(rowIndex, fromCol, rowIndex, toCol);
          const cell = worksheet.getCell(rowIndex, fromCol);
          cell.value = text;
          cell.alignment = { wrapText: true, horizontal: opts.center ? "center" : "left", vertical: "top" };
          if (opts.bold) cell.font = { ...(cell.font || {}), bold: true };
        };

        // Header lines (you can adjust dotted fillers)
        const headerLines = [
          "Nume si prenume persoana juridica/PFA/II/IF/PF .................................................................",
          "Domiciliu persoana juridica/PFA/II/IF/PF .................................................................",
          "(Comuna, judetul)",
          "Ferma (nume/numar, adresa) ....................................................................................",
          "REGISTRU*",
          "de evidenta a tratamentelor cu produse de protectie a plantelor",
          "______________",
          "* Registrul se completeaza dupa fiecare utilizare a acestor produse, cu respectarea normelor privind utilizarea durabila a produselor de protectie a plantelor prevazute în „Codul de bune practici pentru utilizarea în siguranta a produselor de protectie a plantelor“, elaborat de Autoritatea Nationala Fitosanitara, postat pe pagina web a Ministerului Agriculturii si Dezvoltarii Rurale, la sectiunea fitosanitar/utilizarea durabila a produselor de protectie a plantelor.",
        ];

        // Write header
        let currentRow = 1;
        put(currentRow++, headerLines[0]);
        put(currentRow++, headerLines[1]);
        put(currentRow++, headerLines[2]);
        put(currentRow++, headerLines[3]);
        put(currentRow++, headerLines[4], { bold: true, center: true, size: 14 });
        put(currentRow++, headerLines[5], { center: true, bold: true });
        put(currentRow++, headerLines[6], { center: true });
        put(currentRow++, headerLines[7], { italic: true, size: 10 });

        // Leave one blank row before grid
        currentRow++;

        exportDataGrid({
          component: e.component,
          worksheet,
          topLeftCell: { row: currentRow, column: 1 }, // start grid after header
          autoFilterEnabled: true,
        })
          .then((cellRange) => {
            // 2-column footer
            const startFooterRow = cellRange.to.row + 2; // one blank line after grid
            const mid = Math.max(1, Math.floor(colCount / 2));
            const leftFrom = 1,
              leftTo = mid;
            const rightFrom = Math.min(colCount, mid + 1),
              rightTo = colCount;

            // Left column
            putRange(startFooterRow + 0, leftFrom, leftTo, "Semnatura beneficiarului");
            putRange(startFooterRow + 1, leftFrom, leftTo, "");
            putRange(startFooterRow + 2, leftFrom, leftTo, "............................................................");
            putRange(startFooterRow + 3, leftFrom, leftTo, "Data");
            putRange(startFooterRow + 4, leftFrom, leftTo, "............................................................");
            putRange(startFooterRow + 5, leftFrom, leftTo, "");

            // Right column
            putRange(startFooterRow + 0, rightFrom, rightTo, "Avizat");
            putRange(startFooterRow + 1, rightFrom, rightTo, "Oficiul Fitosanitar .................................................");
            putRange(startFooterRow + 2, rightFrom, rightTo, "Șef serviciu/Sef birou/Coordonator ..................................");
            putRange(startFooterRow + 3, rightFrom, rightTo, "Numele si prenumele .................................................");
            putRange(startFooterRow + 4, rightFrom, rightTo, "Semnatura ...........................................................");
            putRange(startFooterRow + 5, rightFrom, rightTo, "Data ................................................................");

            // Optional: freeze header + grid header rows
            worksheet.views = [{ state: "frozen", ySplit: currentRow - 1 }];

            return workbook.xlsx.writeBuffer();
          })
          .then((buffer) => {
            saveAs(new Blob([buffer], { type: "application/octet-stream" }), e.fileName + ".xlsx");
          });
      }
    },
    onSeasonChanged(e) {
      this.local_season_uuid = e.value;
      this.load();
    },
    onDepartmentsChanged(e) {
      this.selected_departments = e.value;
      this.load();
    },
  },
  mounted() {
    const params = {
      limit: 1000,
      sort: [
        {
          selector: "year",
          desc: true,
        },
      ],
    };
    this.getSeasons(params);
    this.getDepartments({ limit: 1000 });

    this.load();
  },
};
</script>

<style scoped lang="scss">
.phytosanitary-grid {
  height: calc(95vh - 200px);
}
</style>
